import React from 'react';
import { FaEdit, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import Card from '../ui/Card';
import Badge from '../ui/Badge';

/**
 * AgentFormStep6 - Review & Publish
 */
const AgentFormStep6 = ({ formData, categories, onEditStep, errors }) => {
  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === parseInt(categoryId));
    return category ? `${category.icon} ${category.name}` : 'Not selected';
  };

  const parseTagsArray = (tags) => {
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') {
      return tags.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0);
    }
    return [];
  };

  const tagsArray = parseTagsArray(formData.tags);

  const memoryTypeLabels = {
    ephemeral: 'Ephemeral (No memory)',
    short_term: 'Short-term (Session memory)',
    long_term: 'Long-term (Persistent memory)',
  };

  const isComplete = formData.title && formData.description && formData.category_id && formData.system_prompt && formData.memory_type;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
          Review & Publish
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Review your agent configuration before publishing. You can edit any section by clicking the edit button.
        </p>
      </div>

      {/* Completion Status */}
      {isComplete ? (
        <Card padding="comfortable" className="bg-green-50 dark:bg-green-900/10 border-2 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <FaCheckCircle className="text-green-500 text-2xl flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                All Required Fields Complete
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Your agent is ready to publish!
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card padding="comfortable" className="bg-yellow-50 dark:bg-yellow-900/10 border-2 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-yellow-500 text-2xl flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                Missing Required Information
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Please complete all required fields before publishing.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Basic Info Section */}
      <Card padding="comfortable">
        <div className="flex justify-between items-start mb-4">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-spooky-ghost-white">
            Basic Information
          </h4>
          <button
            type="button"
            onClick={() => onEditStep(1)}
            className="text-pink-500 hover:text-pink-600 transition flex items-center gap-1 text-sm"
          >
            <FaEdit /> Edit
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Title</label>
            <p className="text-gray-900 dark:text-spooky-ghost-white">
              {formData.title || <span className="text-red-500">Not set</span>}
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Description</label>
            <p className="text-gray-900 dark:text-spooky-ghost-white">
              {formData.description || <span className="text-red-500">Not set</span>}
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Category</label>
            <p className="text-gray-900 dark:text-spooky-ghost-white">
              {formData.category_id ? getCategoryName(formData.category_id) : <span className="text-red-500">Not selected</span>}
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tags</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {tagsArray.length > 0 ? (
                tagsArray.map((tag, index) => (
                  <Badge key={index} variant="agent">{tag}</Badge>
                ))
              ) : (
                <span className="text-gray-500 dark:text-gray-400 text-sm">No tags</span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* System Prompt Section */}
      <Card padding="comfortable">
        <div className="flex justify-between items-start mb-4">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-spooky-ghost-white">
            System Prompt
          </h4>
          <button
            type="button"
            onClick={() => onEditStep(2)}
            className="text-pink-500 hover:text-pink-600 transition flex items-center gap-1 text-sm"
          >
            <FaEdit /> Edit
          </button>
        </div>
        <div className="bg-gray-50 dark:bg-spooky-bg-light p-4 rounded-lg">
          <p className="text-sm text-gray-900 dark:text-spooky-ghost-white whitespace-pre-wrap">
            {formData.system_prompt ? (
              formData.system_prompt.length > 300 ? (
                <>
                  {formData.system_prompt.substring(0, 300)}...
                  <span className="text-gray-500"> ({formData.system_prompt.length} characters)</span>
                </>
              ) : (
                formData.system_prompt
              )
            ) : (
              <span className="text-red-500">Not set</span>
            )}
          </p>
        </div>
      </Card>

      {/* Tools Section */}
      <Card padding="comfortable">
        <div className="flex justify-between items-start mb-4">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-spooky-ghost-white">
            Tools Configuration
          </h4>
          <button
            type="button"
            onClick={() => onEditStep(3)}
            className="text-pink-500 hover:text-pink-600 transition flex items-center gap-1 text-sm"
          >
            <FaEdit /> Edit
          </button>
        </div>
        {formData.tools && formData.tools.length > 0 ? (
          <div className="space-y-2">
            {formData.tools.map((tool, index) => (
              <div key={index} className="bg-gray-50 dark:bg-spooky-bg-light p-3 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-spooky-ghost-white">{tool.name}</p>
                {tool.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{tool.description}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No tools configured</p>
        )}
      </Card>

      {/* Memory Settings Section */}
      <Card padding="comfortable">
        <div className="flex justify-between items-start mb-4">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-spooky-ghost-white">
            Memory Settings
          </h4>
          <button
            type="button"
            onClick={() => onEditStep(4)}
            className="text-pink-500 hover:text-pink-600 transition flex items-center gap-1 text-sm"
          >
            <FaEdit /> Edit
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Memory Type</label>
            <p className="text-gray-900 dark:text-spooky-ghost-white">
              {formData.memory_type ? memoryTypeLabels[formData.memory_type] : <span className="text-red-500">Not set</span>}
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Max Context Window</label>
            <p className="text-gray-900 dark:text-spooky-ghost-white">
              {formData.max_context_window?.toLocaleString()} tokens
            </p>
          </div>
        </div>
      </Card>

      {/* Example Interactions Section */}
      <Card padding="comfortable">
        <div className="flex justify-between items-start mb-4">
          <h4 className="font-semibold text-lg text-gray-900 dark:text-spooky-ghost-white">
            Example Interactions
          </h4>
          <button
            type="button"
            onClick={() => onEditStep(5)}
            className="text-pink-500 hover:text-pink-600 transition flex items-center gap-1 text-sm"
          >
            <FaEdit /> Edit
          </button>
        </div>
        {formData.example_interactions && formData.example_interactions.length > 0 ? (
          <p className="text-gray-700 dark:text-gray-300">
            {formData.example_interactions.length} example{formData.example_interactions.length !== 1 ? 's' : ''} provided
          </p>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No examples provided (optional)</p>
        )}
      </Card>

      {/* Publish Toggle */}
      <Card padding="comfortable" className="bg-pink-50 dark:bg-pink-900/10 border-2 border-pink-200 dark:border-pink-800">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="is_published"
            checked={formData.is_published}
            onChange={(e) => {
              const event = { target: { name: 'is_published', value: e.target.checked } };
              // This will be handled by the parent component's onChange
              if (formData.onChange) formData.onChange(event);
            }}
            className="w-5 h-5 accent-pink-500"
          />
          <div>
            <p className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
              Publish this agent
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Make this agent publicly visible to the community
            </p>
          </div>
        </label>
      </Card>
    </div>
  );
};

export default AgentFormStep6;
